import { LockOpenOutlined } from '@mui/icons-material';
import { Stack } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import * as React from 'react';
import { useCallback } from 'react';
import { BreadcrumbMeta } from 'v2/components/Breadcrumb/Breadcrumb';
import EmptyStateOverview from 'v2/components/EmptyStateOverview/EmptyStateOverview';
import useModalContext from 'v2/components/Modal/useModalContext';
import OverviewContent from 'v2/components/OverviewContent/OverviewContent';
import PageTabs from 'v2/components/PageTabs/PageTabs';
import SearchAndFilters from 'v2/components/SearchAndFilters/SearchAndFilters';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import { WIZARD_MODAL_DEFAULT_OPTIONS } from 'v2/constants';
import useDeviceGroups from 'v2/hooks/useDeviceGroups/useDeviceGroups';
import { createDeviceTabs } from '../createDeviceTabs';
import TableWithData from './components/TableWithData';
import { useDeviceGroupsSearchAndFilters } from './useDeviceGroupsSearchAndFilters';
import AddDeviceGroupWizard from './wizards/AddDeviceGroupWizard';

const DeviceGroups: React.FC = () => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const { openModal } = useModalContext();
  const tabs = createDeviceTabs(translation);

  const {
    deviceGroupsWithUserGroups,
    allDoors,
    allVisitorGroups,
    loading,
    createDeviceGroup,
    assignUserGroupsToDeviceGroup,
    updateDeviceGroupWithDoors
  } = useDeviceGroups({
    handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' })
  });

  const { filterStr, setFilterStr, filteredDeviceGroups, countCaption, chips, sections } =
    useDeviceGroupsSearchAndFilters({ deviceGroups: deviceGroupsWithUserGroups });

  const breadcrumb = {
    title: translation.device_groups,
    crumbs: [
      {
        text: translation.devices,
        path: '/devices'
      }
    ]
  };

  const openAddModal = useCallback(() => {
    openModal(
      <AddDeviceGroupWizard
        deviceGroups={deviceGroupsWithUserGroups}
        allDoors={allDoors}
        allVisitorGroups={allVisitorGroups}
        createDeviceGroup={createDeviceGroup}
        assignUserGroupsToDeviceGroup={assignUserGroupsToDeviceGroup}
        updateDeviceGroupWithDoors={updateDeviceGroupWithDoors}
      />,
      WIZARD_MODAL_DEFAULT_OPTIONS
    );
  }, [
    openModal,
    deviceGroupsWithUserGroups,
    allDoors,
    allVisitorGroups,
    createDeviceGroup,
    assignUserGroupsToDeviceGroup,
    updateDeviceGroupWithDoors
  ]);

  return (
    <>
      <BreadcrumbMeta breadcrumb={breadcrumb} />
      <PageTabs tabs={tabs} />
      <Stack spacing={2} mt={2}>
        <SearchAndFilters
          filterStr={filterStr}
          onFilterStrChange={setFilterStr}
          sections={sections}
          chips={chips}
          countCaption={countCaption}
          actionButton={{ id: 'add-device-group-button', label: translation.add_group, onClick: openAddModal }}
        />
        <OverviewContent
          loading={loading}
          hasData={deviceGroupsWithUserGroups.length > 0}
          hasResults={filteredDeviceGroups.length > 0}
          table={<TableWithData rows={filteredDeviceGroups} loading={loading} />}
          noResults={
            <EmptyStateOverview
              icon={LockOpenOutlined}
              title={translation.no_results}
              content={translation.try_less_specific_filters}
            />
          }
          noData={
            <EmptyStateOverview
              icon={LockOpenOutlined}
              title={translation.no_device_groups}
              content={translation.lets_get_started_by_creating_your_first_device_group}
              button={{ label: translation.add_group, onClick: openAddModal }}
            />
          }
        />
      </Stack>
    </>
  );
};

export default DeviceGroups;
